import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Logo, LogoMark } from "@/components/Logo";
import { formatCurrency } from "@/lib/format";
import { submitMonriForm, type MonriFormParams } from "@/lib/monri";
import { cn } from "@/lib/utils";
import type { ReturnOutcome } from "@/lib/payment-return-url";
import { useConfirmMonriReturn } from "@/hooks/useConfirmMonriReturn";
import {
  ArrowRight,
  Bank,
  CaretLeft,
  CheckCircle,
  CircleNotch,
  Clock,
  Copy,
  CreditCard,
  LockKey,
  Receipt,
  SealCheck,
  WarningCircle,
} from "@phosphor-icons/react";

interface Meta {
  ledgerId?: string;
  status: "pending" | "completed" | "failed" | "expired" | "exempt";
  receiptNumber?: string | null;
  amountGross?: number;
  amountNet?: number;
  amountVat?: number;
  vatRate?: number;
  currency?: string;
  planType?: "semiannual" | "annual" | "custom" | null;
  planLocked?: boolean;
  planOptions?: Record<"semiannual" | "annual", { amountNet: number; amountVat: number; amountGross: number }>;
  therapistName?: string;
  paymentLinkExpiresAt?: string | null;
  billingComplete?: boolean;
  billingType?: "company" | "individual";
  /** Present once bank transfer was chosen; card is no longer offered then. */
  bank?: BankInfo;
}

interface BankInfo {
  name: string;
  iban: string;
  accountName: string;
  amount: number;
  currency: string;
  purpose: string;
}

const PLAN_LABEL: Record<string, string> = {
  semiannual: "Polugodišnja pretplata",
  annual: "Godišnja pretplata",
  custom: "Pretplata",
};

/**
 * The subscription pay page. `returnStatus` is the validated outcome of a Monri
 * return (path `/subscription/pay/<token>/<outcome>` or the legacy
 * `?status=`), "unknown" on a plain visit.
 */
export function SubscriptionPayPage({ token, returnStatus }: { token: string; returnStatus: ReturnOutcome }) {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState<"monri" | "bank_transfer" | null>(null);
  const [bankInfo, setBankInfo] = useState<BankInfo | null>(null);
  const [planChoice, setPlanChoice] = useState<"" | "semiannual" | "annual">("");
  const [showBilling, setShowBilling] = useState(false);
  const [cancelDismissed, setCancelDismissed] = useState(false);

  // Monri signs the success redirect; confirming it covers a lost callback.
  // The polling below picks up the completed row.
  useConfirmMonriReturn(returnStatus === "success");

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function load() {
      try {
        const r = await fetch(`/api/subscription/pay/${token}`);
        const body = (await r.json()) as Meta;
        if (cancelled) return;
        setMeta(body);
        if (body.status === "pending") {
          setShowBilling(!body.billingComplete);
          if (body.planType !== "custom" && body.planType) setPlanChoice(body.planType);
          // Returning from an approved Monri payment before the webhook lands
          // leaves status "pending"; keep polling so we don't re-show the picker
          // (which would let the therapist charge a second time).
          if (returnStatus === "success") {
            timer = setTimeout(load, 2500);
          }
        }
      } catch {
        if (!cancelled) setError("Greška pri učitavanju");
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, returnStatus]);

  if (!meta) {
    return (
      <PayShell>
        <PayCard>
          <div className="flex flex-col items-center py-10 text-center">
            <CircleNotch className="size-7 animate-spin" style={{ color: "var(--color-brand)" }} />
            <p className="mt-4 text-sm text-muted-foreground">
              {error ? error : "Učitavanje…"}
            </p>
          </div>
        </PayCard>
      </PayShell>
    );
  }

  if (meta.status === "completed") {
    return (
      <PayShell>
        <PayCard>
          <StatusBlock
            tone="brand"
            icon={<SealCheck weight="fill" className="size-7" />}
            title="Uspješno plaćena pretplata"
            desc={
              <>
                Račun broj{" "}
                <span className="font-medium text-foreground">{meta.receiptNumber}</span>
              </>
            }
          />
          <a
            href={`/api/subscription/receipts/${meta.ledgerId}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ background: "var(--color-brand)" }}
          >
            <Receipt className="size-4" />
            Preuzmi PDF račun
          </a>
        </PayCard>
        <TrustFooter />
      </PayShell>
    );
  }

  if (meta.status === "expired") {
    return (
      <PayShell>
        <PayCard>
          <StatusBlock
            tone="amber"
            icon={<Clock weight="fill" className="size-7" />}
            title="Link je istekao"
            desc="Javite se administratoru da Vam generiše novi link za plaćanje."
          />
        </PayCard>
        <TrustFooter />
      </PayShell>
    );
  }

  if (meta.status === "exempt") {
    return (
      <PayShell>
        <PayCard>
          <StatusBlock
            tone="brand"
            icon={<SealCheck weight="fill" className="size-7" />}
            title="Pretplata nije potrebna"
            desc="Oslobođeni ste plaćanja pretplate, pa ovaj link više ne važi."
          />
        </PayCard>
        <TrustFooter />
      </PayShell>
    );
  }

  if (meta.status === "failed") {
    return (
      <PayShell>
        <PayCard>
          <StatusBlock
            tone="rose"
            icon={<WarningCircle weight="fill" className="size-7" />}
            title="Plaćanje neuspjelo"
            desc="Javite se administratoru za pomoć pri plaćanju pretplate."
          />
        </PayCard>
        <TrustFooter />
      </PayShell>
    );
  }

  // Returned from an approved card payment; the webhook has not yet flipped the
  // ledger to "completed". Show a processing state (the effect keeps polling)
  // instead of the pay-method picker so the payment can't be submitted twice.
  if (returnStatus === "success" && meta.status === "pending") {
    return (
      <PayShell>
        <PayCard>
          <StatusBlock
            tone="brand"
            icon={<CircleNotch className="size-7 animate-spin" />}
            title="Uplatu obrađujemo"
            desc="Vašu uplatu obrađujemo. Ovo može potrajati par minuta. Stranica će se automatski osvježiti kad plaćanje bude potvrđeno."
          />
        </PayCard>
        <TrustFooter />
      </PayShell>
    );
  }

  // User backed out at the gateway. Acknowledge without re-charging; let them
  // retry explicitly.
  if (returnStatus === "cancelled" && !cancelDismissed) {
    return (
      <PayShell>
        <PayCard>
          <StatusBlock
            tone="amber"
            icon={<WarningCircle weight="fill" className="size-7" />}
            title="Plaćanje otkazano"
            desc="Plaćanje nije dovršeno pa pretplata nije naplaćena. Možete pokušati ponovo."
          />
          <Button
            className="mt-6 h-12 w-full rounded-xl"
            onClick={() => setCancelDismissed(true)}
          >
            Pokušaj ponovo
            <ArrowRight data-icon="inline-end" />
          </Button>
        </PayCard>
        <TrustFooter />
      </PayShell>
    );
  }

  if (showBilling) {
    return (
      <BillingForm
        token={token}
        billingType={meta.billingType ?? "individual"}
        onComplete={() => setShowBilling(false)}
      />
    );
  }

  async function pickMethod(m: "monri" | "bank_transfer") {
    setMethod(m);
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/subscription/pay/${token}/select-method`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method: m, planType: planChoice || undefined }),
      });
      if (!res.ok) {
        const body = (await res.json()) as { error?: string };
        throw new Error(body.error || "Podaci nisu sačuvani. Pokušajte ponovo.");
      }
      const body = (await res.json()) as { monri?: MonriFormParams; bank?: BankInfo };
      if (m === "monri") {
        if (!body.monri) throw new Error("Plaćanje karticom trenutno nije dostupno. Pokušajte ponovo.");
        // Monri's form only accepts POST. Keep the spinner while the page leaves.
        submitMonriForm(body.monri);
        return;
      }
      if (body.bank) setBankInfo(body.bank);
    } catch (e) {
      setError((e as Error).message);
    }
    setSubmitting(false);
    setMethod(null);
  }

  // Once bank transfer is chosen (now or on an earlier visit) the row is pinned
  // to it: the server refuses card, so only the wire instructions are shown,
  // with no way back to the method picker.
  const shownBank = bankInfo ?? meta.bank ?? null;
  if (shownBank) {
    return (
      <PayShell>
        <PayCard
          title="Uplata putem žiro računa"
          subtitle="Prepišite podatke u svoju banku ili mobilnu aplikaciju"
        >
          <div className="overflow-hidden rounded-2xl border" style={{ borderColor: "var(--color-warm-border)" }}>
            <DetailRow label="Primalac" value={shownBank.accountName} />
            <DetailRow label="IBAN / Žiro" value={shownBank.iban} mono copyable />
            <DetailRow label="Banka" value={shownBank.name} />
            <DetailRow
              label="Iznos"
              value={formatCurrency(shownBank.amount, shownBank.currency)}
              highlight
            />
            <DetailRow label="Svrha uplate" value={shownBank.purpose} copyable last />
          </div>

          <div
            className="mt-5 flex items-start gap-3 rounded-xl p-3.5 text-sm"
            style={{ background: "var(--color-brand-bg-light)" }}
          >
            <Clock className="mt-0.5 size-4 shrink-0" style={{ color: "var(--color-brand)" }} />
            <p className="text-muted-foreground">
              Nakon uplate, administrator ručno potvrđuje i aktivira Vašu pretplatu, obično
              unutar <span className="font-medium text-foreground">1–2 radna dana</span>.
            </p>
          </div>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Za ovu uplatu je odabrana uplata na žiro račun. Ako ipak želite platiti karticom, a još
            niste uplatili, javite se administratoru za novi link.
          </p>
        </PayCard>
        <TrustFooter />
      </PayShell>
    );
  }

  // The server decides: only a link whose creator chose the plan is locked.
  // planType alone can't tell, every row carries one (semiannual by default).
  const isLocked = !!meta.planLocked;
  const activePlan = isLocked ? meta.planType : planChoice;
  const planReady = isLocked || !!planChoice;
  const shown =
    !isLocked && planChoice && meta.planOptions ? meta.planOptions[planChoice] : meta;

  return (
    <PayShell>
      <PayCard
        title="Uplata pretplate"
        subtitle={
          meta.therapistName ? (
            <>Terapeut: <span className="font-medium text-white">{meta.therapistName}</span></>
          ) : undefined
        }
      >
        {/* Plan selector — only when the link doesn't lock a plan */}
        {!isLocked && (
          <div className="mb-5 space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Izaberite plan
            </Label>
            <Select
              value={planChoice}
              onValueChange={(v) => setPlanChoice(v as "semiannual" | "annual")}
            >
              <SelectTrigger className="h-12 w-full rounded-xl">
                <SelectValue placeholder="Izaberite plan pretplate…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="semiannual">Polugodišnja</SelectItem>
                <SelectItem value="annual">Godišnja</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Amount — hero total + breakdown */}
        <div
          className="rounded-2xl p-5"
          style={{ background: "var(--color-brand-bg-light)" }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Ukupno za uplatu</span>
            {activePlan && (
              <span
                className="rounded-full px-2.5 py-1 text-[0.7rem] font-medium"
                style={{ background: "var(--color-brand-bg)", color: "var(--color-brand-dark)" }}
              >
                {PLAN_LABEL[activePlan] ?? "Pretplata"}
              </span>
            )}
          </div>
          <div
            className="mt-1 text-4xl leading-none tracking-tight sm:text-[2.75rem]"
            style={{ fontFamily: "var(--font-heading)", color: "var(--color-brand-dark)" }}
          >
            {formatCurrency(shown.amountGross, meta.currency)}
          </div>
          <dl
            className="mt-4 space-y-1.5 border-t pt-3 text-sm"
            style={{ borderColor: "var(--color-brand-bg)" }}
          >
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Osnovica</dt>
              <dd className="text-foreground">{formatCurrency(shown.amountNet, meta.currency)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                PDV ({((meta.vatRate ?? 0) * 100).toFixed(0)}%)
              </dt>
              <dd className="text-foreground">{formatCurrency(shown.amountVat, meta.currency)}</dd>
            </div>
          </dl>
        </div>

        {/* Payment methods */}
        <div className="mt-6">
          <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Način plaćanja
          </p>
          <div className="space-y-3">
            <MethodCard
              emphasis
              icon={<CreditCard weight="fill" className="size-5" />}
              title="Plati karticom (Monri)"
              desc="Visa · Mastercard · Maestro, odmah aktivno"
              onClick={() => pickMethod("monri")}
              disabled={submitting || !planReady}
              loading={submitting && method === "monri"}
            />
            <MethodCard
              icon={<Bank weight="fill" className="size-5" />}
              title="Plati žiralno (Bank transfer)"
              desc="Uplatnica / virman, potvrda za 1–2 radna dana"
              onClick={() => pickMethod("bank_transfer")}
              disabled={submitting || !planReady}
              loading={submitting && method === "bank_transfer"}
            />
          </div>
          {!planReady && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Izaberite plan da nastavite na plaćanje.
            </p>
          )}
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </PayCard>
      <TrustFooter />
    </PayShell>
  );
}

/* ----------------------------------------------------------------------- */
/* Layout primitives                                                       */
/* ----------------------------------------------------------------------- */

const GRAIN_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E\")";

function PayShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-[100dvh] w-full items-center justify-center overflow-hidden bg-background px-4 py-8 safe-area-pb sm:px-6 sm:py-12">
      {/* Petrol glow from the top */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(130% 70% at 50% -15%, var(--color-brand-bg), transparent 60%)",
        }}
      />
      {/* Subtle grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35] mix-blend-multiply"
        style={{ backgroundImage: GRAIN_BG }}
      />
      <div className="animate-ink-rise relative w-full max-w-md">{children}</div>
    </div>
  );
}

function PayCard({
  title,
  subtitle,
  onBack,
  children,
}: {
  title?: string;
  subtitle?: React.ReactNode;
  onBack?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="overflow-hidden rounded-3xl border bg-card"
      style={{
        borderColor: "var(--color-warm-border)",
        boxShadow: "0 28px 70px -32px var(--color-brand-shadow)",
      }}
    >
      {/* Petrol header band */}
      <div
        className="relative overflow-hidden px-6 pb-7 pt-6 text-white sm:px-8"
        style={{
          background:
            "linear-gradient(150deg, var(--color-brand-dark), var(--color-brand) 62%, var(--color-brand-dark))",
        }}
      >
        {/* Gold halo */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 60% at 88% 0%, oklch(74.72% 0.089 82.39 / 0.22), transparent 70%)",
          }}
        />
        {/* Oversized logo-mark motif */}
        <LogoMark
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-14 size-52 opacity-[0.07]"
          style={{ ["--color-brand" as string]: "white" }}
        />
        {/* Grain */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.5] mix-blend-soft-light"
          style={{ backgroundImage: GRAIN_BG }}
        />

        <div className="relative flex items-center justify-between">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="-ml-1 flex items-center gap-1 rounded-lg px-1 py-1 text-sm text-white/80 transition-colors hover:text-white"
            >
              <CaretLeft className="size-4" /> Nazad
            </button>
          ) : (
            <Logo
              className="h-6 w-auto text-white"
              title="Psihotron"
              style={{ ["--color-brand" as string]: "var(--color-accent-gold)" }}
            />
          )}
          <span className="flex items-center gap-1.5 text-[0.7rem] font-medium text-white/65">
            <LockKey weight="fill" className="size-3.5" style={{ color: "var(--color-accent-gold)" }} />
            Sigurno
          </span>
        </div>

        {title && (
          <h1
            className="relative mt-6 text-2xl tracking-tight sm:text-[1.7rem]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <p className="relative mt-1.5 text-sm text-white/70">{subtitle}</p>
        )}
      </div>

      {/* Body */}
      <div className="px-6 py-6 sm:px-8 sm:py-7">{children}</div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Pieces                                                                  */
/* ----------------------------------------------------------------------- */

function MethodCard({
  icon,
  title,
  desc,
  onClick,
  disabled,
  loading,
  emphasis,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  emphasis?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "group flex w-full items-center gap-4 rounded-2xl border bg-card p-4 text-left transition-all",
        "hover:shadow-[0_10px_30px_-18px_var(--color-brand-shadow)] active:scale-[0.99]",
        "disabled:pointer-events-none disabled:opacity-50 sm:p-5"
      )}
      style={{ borderColor: emphasis ? "var(--color-brand)" : "var(--color-warm-border)" }}
    >
      <span
        className="flex size-12 shrink-0 items-center justify-center rounded-xl"
        style={{
          background: emphasis ? "var(--color-brand)" : "var(--color-brand-bg-light)",
          color: emphasis ? "white" : "var(--color-brand)",
        }}
      >
        {loading ? <CircleNotch className="size-5 animate-spin" /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-foreground">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{desc}</span>
      </span>
      <ArrowRight
        className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
        style={{ color: emphasis ? "var(--color-brand)" : "var(--color-muted-foreground, currentColor)" }}
      />
    </button>
  );
}

function DetailRow({
  label,
  value,
  mono,
  copyable,
  highlight,
  last,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
  highlight?: boolean;
  last?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <div
      className={cn("flex items-center justify-between gap-3 px-4 py-3", !last && "border-b")}
      style={{ borderColor: "var(--color-warm-border)" }}
    >
      <div className="min-w-0">
        <div className="text-[0.7rem] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div
          className={cn(
            "mt-0.5 break-words text-sm",
            mono && "font-mono text-xs",
            highlight ? "font-semibold" : "text-foreground"
          )}
          style={highlight ? { color: "var(--color-brand-dark)" } : undefined}
        >
          {value}
        </div>
      </div>
      {copyable && (
        <button
          type="button"
          onClick={copy}
          aria-label={`Kopiraj ${label}`}
          className="flex size-9 shrink-0 items-center justify-center rounded-lg border transition-colors hover:bg-muted"
          style={{ borderColor: "var(--color-warm-border)" }}
        >
          {copied ? (
            <CheckCircle weight="fill" className="size-4" style={{ color: "var(--color-brand)" }} />
          ) : (
            <Copy className="size-4 text-muted-foreground" />
          )}
        </button>
      )}
    </div>
  );
}

function StatusBlock({
  icon,
  title,
  desc,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  desc: React.ReactNode;
  tone: "brand" | "amber" | "rose";
}) {
  const palette = {
    brand: { bg: "var(--color-brand-bg)", fg: "var(--color-brand)" },
    amber: { bg: "oklch(74.72% 0.089 82.39 / 0.18)", fg: "oklch(55% 0.12 70)" },
    rose: { bg: "oklch(0.577 0.245 27.325 / 0.12)", fg: "var(--color-destructive)" },
  }[tone];
  return (
    <div className="flex flex-col items-center py-2 text-center">
      <span
        className="flex size-16 items-center justify-center rounded-2xl"
        style={{ background: palette.bg, color: palette.fg }}
      >
        {icon}
      </span>
      <h2
        className="mt-5 text-xl tracking-tight text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {title}
      </h2>
      <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">{desc}</p>
    </div>
  );
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
      <WarningCircle className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function TrustFooter() {
  return (
    <div className="mt-6 flex items-center justify-center gap-2.5 text-xs text-muted-foreground">
      <span aria-hidden className="grid grid-cols-2 gap-0.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <span
            key={i}
            className="size-1 rounded-full"
            style={{ background: "var(--color-accent-gold)", opacity: 0.7 }}
          />
        ))}
      </span>
      Psihotron · Sigurna obrada plaćanja
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Billing form                                                            */
/* ----------------------------------------------------------------------- */

function BillingForm({
  token,
  billingType,
  onComplete,
}: {
  token: string;
  /** Tip kupca je zadan linkom; server ga koristi bez obzira na formu, pa se ovdje ne bira. */
  billingType: "company" | "individual";
  onComplete: () => void;
}) {
  const [v, setV] = useState({
    businessName: "",
    businessAddress: "",
    businessTaxId: "",
    businessVatId: "",
    personalAddress: "",
    personalTaxId: "",
  });
  const type = billingType;
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const body =
        type === "company"
          ? {
              businessName: v.businessName.trim(),
              businessAddress: v.businessAddress.trim(),
              businessTaxId: v.businessTaxId.trim(),
              businessVatId: v.businessVatId.trim() || undefined,
            }
          : {
              personalAddress: v.personalAddress.trim(),
              personalTaxId: v.personalTaxId.trim(),
            };
      const res = await fetch(`/api/subscription/pay/${token}/billing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const body = (await res.json()) as { error?: string };
        throw new Error(body.error || "Podaci nisu sačuvani. Pokušajte ponovo.");
      }
      onComplete();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PayShell>
      <PayCard
        title="Podaci za fakturu"
        subtitle="Ovi podaci se pojavljuju na Vašem računu"
      >
        <p className="text-sm text-muted-foreground">
          {type === "individual" ? "Račun glasi na Vas kao fizičko lice." : "Račun glasi na Vašu firmu."}
        </p>

        <div className="mt-5 space-y-3">
          {type === "individual" ? (
            <>
              <Field label="Adresa stanovanja">
                <Input
                  placeholder="npr. Maršala Tita 1, Sarajevo"
                  value={v.personalAddress}
                  onChange={(e) => setV({ ...v, personalAddress: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </Field>
              <Field label="JMBG">
                <Input
                  placeholder="13 cifara"
                  inputMode="numeric"
                  maxLength={13}
                  value={v.personalTaxId}
                  onChange={(e) => setV({ ...v, personalTaxId: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Naziv firme">
                <Input
                  placeholder="Naziv pravnog lica"
                  value={v.businessName}
                  onChange={(e) => setV({ ...v, businessName: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </Field>
              <Field label="Adresa firme">
                <Input
                  placeholder="Sjedište firme"
                  value={v.businessAddress}
                  onChange={(e) => setV({ ...v, businessAddress: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </Field>
              <Field label="JIB">
                <Input
                  placeholder="13 cifara"
                  inputMode="numeric"
                  maxLength={13}
                  value={v.businessTaxId}
                  onChange={(e) => setV({ ...v, businessTaxId: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </Field>
              <Field label="PDV broj (opcionalno)">
                <Input
                  placeholder="PDV broj"
                  value={v.businessVatId}
                  onChange={(e) => setV({ ...v, businessVatId: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </Field>
            </>
          )}
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        <Button
          className="mt-6 h-12 w-full rounded-xl"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <CircleNotch className="animate-spin" data-icon="inline-start" />
          ) : null}
          {submitting ? "Snimam…" : "Nastavi na plaćanje"}
          {!submitting && <ArrowRight data-icon="inline-end" />}
        </Button>
      </PayCard>
      <TrustFooter />
    </PayShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5 text-left">
      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
